import React, { useState, useRef, useEffect } from 'react';
import { Mic, Upload, X, Play, Square } from 'lucide-react';

const SamplePad = ({ 
  padIndex, 
  deck,
  audioContext,
  isEnabled = true 
}) => {
  const [sample, setSample] = useState(null); // { name, buffer, duration }
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const sourceRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  
  const MAX_DURATION = 5; // 5 seconds max
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (sourceRef.current) {
        try { sourceRef.current.stop(); } catch (_) { /* Already stopped */ }
      }
    };
  }, []);
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !audioContext) return;
    
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Trim to max 5 seconds
      const duration = Math.min(audioBuffer.duration, MAX_DURATION);
      
      setSample({
        name: file.name.substring(0, 10),
        buffer: audioBuffer,
        duration
      });
      setShowMenu(false);
    } catch (err) {
      console.error('Failed to load audio file:', err);
    }
  };
  
  // Start recording from microphone
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        
        if (audioContext) {
          try {
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            setSample({
              name: `REC ${padIndex + 1}`,
              buffer: audioBuffer,
              duration: Math.min(audioBuffer.duration, MAX_DURATION)
            });
          } catch (err) {
            console.error('Failed to decode recording:', err);
          }
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setShowMenu(false);
      
      // Track recording time and auto-stop at 5 seconds
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_DURATION - 0.1) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 0.1;
        });
      }, 100);
      
    } catch (err) {
      console.error('Failed to access microphone:', err);
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };
  
  // Play sample
  const playSample = () => {
    if (!sample?.buffer || !audioContext || isPlaying) return;
    
    try {
      sourceRef.current = audioContext.createBufferSource();
      sourceRef.current.buffer = sample.buffer;
      sourceRef.current.connect(audioContext.destination);
      
      sourceRef.current.onended = () => {
        setIsPlaying(false);
      };
      
      sourceRef.current.start(0, 0, sample.duration);
      setIsPlaying(true);
    } catch (err) {
      console.error('Failed to play sample:', err);
    }
  };
  
  // Stop sample
  const stopSample = () => {
    if (sourceRef.current && isPlaying) {
      try {
        sourceRef.current.stop();
      } catch (_) { /* Already stopped */ }
      setIsPlaying(false);
    }
  };
  
  // Clear sample
  const clearSample = () => {
    stopSample();
    setSample(null);
    setShowMenu(false);
  };
  
  // Handle pad click
  const handlePadClick = () => {
    if (!isEnabled) return;
    
    if (sample) {
      if (isPlaying) {
        stopSample();
      } else {
        playSample();
      }
    } else {
      setShowMenu(!showMenu);
    }
  };
  
  return (
    <div className={`sample-pad-container ${deck.toLowerCase()}`}>
      <button
        className={`sample-pad ${sample ? 'has-sample' : ''} ${isPlaying ? 'playing' : ''} ${isRecording ? 'recording' : ''}`}
        onClick={handlePadClick}
        onContextMenu={(e) => {
          e.preventDefault();
          if (sample) setShowMenu(!showMenu);
        }}
        disabled={!isEnabled}
        data-testid={`sample-pad-${deck}-${padIndex}`}
      >
        {isRecording ? (
          <div className="recording-indicator">
            <div className="rec-dot" />
            <span>{recordingTime.toFixed(1)}s</span>
          </div>
        ) : sample ? (
          <div className="sample-info">
            <span className="sample-name">{sample.name}</span>
            {isPlaying && <div className="playing-wave" />}
          </div>
        ) : (
          <span className="pad-number">{padIndex + 1}</span>
        )}
      </button>
      
      {/* Menu for loading/recording */}
      {showMenu && (
        <>
          <div className="pad-menu-overlay" onClick={() => setShowMenu(false)} />
          <div className="pad-menu">
            {!sample ? (
              <>
                <button onClick={() => fileInputRef.current?.click()}>
                  <Upload size={14} />
                  Upload
                </button>
                <button onClick={startRecording}>
                  <Mic size={14} />
                  Record
                </button>
              </>
            ) : (
              <>
                <button onClick={playSample} disabled={isPlaying}>
                  <Play size={14} />
                  Play
                </button>
                <button onClick={clearSample} className="delete">
                  <X size={14} />
                  Clear
                </button>
              </>
            )}
          </div>
        </>
      )}
      
      {/* Recording stop button */}
      {isRecording && (
        <button className="stop-recording-btn" onClick={stopRecording}>
          <Square size={12} />
        </button>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default SamplePad;
